import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  ShoppingBagIcon,
  UserGroupIcon,
  TruckIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

import api from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Redirect admin and vendor to their dashboards
  useEffect(() => {
    if (user && user.roles) {
      if (user.roles.some(role => role.name === 'admin')) {
        navigate('/admin/dashboard');
      } else if (user.roles.some(role => role.name === 'vendor')) {
        navigate('/vendor/dashboard');
      }
    }
  }, [user, navigate]);

  // جلب المنتجات المميزة
  const { data: featuredProducts, isLoading: productsLoading } = useQuery(
    'featured-products',
    () => api.get('/products?limit=8').then(res => res.data.data.products),
    { 
      enabled: true,
      onError: (error) => {
        console.error('خطأ في جلب المنتجات:', error);
      }
    }
  );

  // جلب المتاجر المميزة
  const { data: featuredVendors, isLoading: vendorsLoading } = useQuery(
    'featured-vendors',
    () => api.get('/vendors?limit=6').then(res => res.data.data.vendors),
    { 
      enabled: true,
      onError: (error) => {
        console.error('خطأ في جلب المتاجر:', error);
      }
    }
  );

  const features = [
    {
      icon: ShoppingBagIcon,
      title: 'منتجات متنوعة',
      description: 'آلاف المنتجات من مختلف الفئات والبائعين'
    },
    {
      icon: UserGroupIcon,
      title: 'بائعون موثوقون',
      description: 'بائعون معتمدون ومراجعون بعناية'
    },
    {
      icon: TruckIcon,
      title: 'توصيل سريع',
      description: 'توصيل لجميع أنحاء السودان'
    },
    {
      icon: ShieldCheckIcon,
      title: 'دفع آمن',
      description: 'طرق دفع متعددة وآمنة'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              أمازول
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              منصة التجارة الإلكترونية الرائدة في السودان
            </p>
            <p className="text-lg mb-10 text-primary-200 max-w-2xl mx-auto">
              اكتشف آلاف المنتجات من بائعين موثوقين، أو انضم كبائع وابدأ رحلتك التجارية معنا
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/products"
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                تصفح المنتجات
              </Link>
              <Link
                to="/vendor/register"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors"
              >
                انضم كبائع
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              لماذا تختار أمازول؟
            </h2>
            <p className="text-lg text-gray-600">
              نوفر لك تجربة تسوق مميزة وآمنة
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {productsLoading ? (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-600">جاري تحميل المنتجات...</p>
            </div>
          </div>
        </section>
      ) : featuredProducts && featuredProducts.length > 0 ? (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">
                منتجات مميزة
              </h2>
              <Link
                to="/products"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                عرض الكل ←
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <div key={product.id} className="product-card">
                  <Link to={`/products/${product.id}`}>
                    <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-t-lg bg-gray-200">
                      {product.images && product.images.length > 0 && product.images[0].url ? (
                        <img
                          src={product.images[0].url}
                          alt={product.name}
                          className="h-48 w-full object-cover object-center group-hover:opacity-75"
                        />
                      ) : (
                        <div className="h-48 w-full flex items-center justify-center bg-gray-200">
                          <ShoppingBagIcon className="w-16 h-16 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-medium text-gray-900 mb-2">
                        {product.name}
                      </h3>
                      <p className="text-lg font-bold text-primary-600">
                        {product.price ? parseFloat(product.price).toLocaleString() : '0'} جنيه
                      </p>
                      {product.rating_average > 0 && (
                        <div className="flex items-center mt-1">
                          <span className="text-yellow-400 text-sm">★</span>
                          <span className="text-sm text-gray-600 mr-1">
                            {parseFloat(product.rating_average).toFixed(1)}
                          </span>
                          <span className="text-xs text-gray-500 mr-1">
                            ({product.rating_count || 0})
                          </span>
                        </div>
                      )}
                      {product.vendor && (
                        <p className="text-xs text-gray-500 mt-1">
                          {product.vendor.store_name}
                        </p>
                      )}
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Featured Vendors */}
      {vendorsLoading ? (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-600">جاري تحميل المتاجر...</p>
            </div>
          </div>
        </section>
      ) : featuredVendors && featuredVendors.length > 0 ? (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">
                متاجر مميزة
              </h2>
              <Link
                to="/vendors"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                عرض الكل ←
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {featuredVendors.map((vendor) => (
                <div key={vendor.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <Link to={`/store/${vendor.store_slug}`}>
                    <div className="flex items-center mb-4">
                      {vendor.logo_url ? (
                        <img
                          src={vendor.logo_url}
                          alt={vendor.store_name}
                          className="w-12 h-12 rounded-full object-cover ml-3"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center ml-3">
                          <span className="text-primary-600 font-bold text-lg">
                            {vendor.store_name?.charAt(0) || 'م'}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {vendor.store_name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {vendor.category}
                        </p>
                      </div>
                    </div>
                    {vendor.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {vendor.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-yellow-400">★</span>
                        <span className="text-sm text-gray-600 mr-1">
                          {vendor.rating_average ? parseFloat(vendor.rating_average).toFixed(1) : '0.0'}
                        </span>
                      </div>
                      <span className="text-primary-600 text-sm font-medium">
                        زيارة المتجر ←
                      </span>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* CTA Section */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            هل أنت بائع؟
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            انضم إلى آلاف البائعين واعرض منتجاتك لملايين العملاء
          </p>
          <Link
            to="/vendor/register"
            className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
          >
            ابدأ البيع الآن
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;